import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.#setupCarouselHeight();
  }

  #controller = new AbortController();
  #resizeObserver = /** @type {ResizeObserver | null} */ (null);
  #heightAnimationFrame = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#heightAnimationFrame);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #setupCarouselHeight() {
    if (this.presentation !== 'carousel' || !this.querySelector('slideshow-controls[thumbnails]')) return;

    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, {
      signal: this.#controller.signal,
    });

    if ('ResizeObserver' in window && this.slideshow?.refs.scroller) {
      this.#resizeObserver = new ResizeObserver(this.#scheduleCarouselHeightSync);
      this.#resizeObserver.observe(this.slideshow.refs.scroller);
    }

    this.#scheduleCarouselHeightSync();
  }

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    this.#scheduleCarouselHeightSync(event.detail.index);
  };

  /**
   * @param {number | ResizeObserverEntry[]} [index]
   */
  #scheduleCarouselHeightSync = (index) => {
    const selectedIndex = Number.isInteger(index) ? /** @type {number} */ (index) : undefined;

    cancelAnimationFrame(this.#heightAnimationFrame);
    this.#heightAnimationFrame = requestAnimationFrame(() => {
      this.#syncCarouselHeight(selectedIndex);
    });
  };

  /**
   * @param {number} [index]
   */
  #syncCarouselHeight(index) {
    const slideshow = this.slideshow;
    const scroller = slideshow?.refs.scroller;
    const slides = slideshow?.refs.slides;

    if (!slideshow || !scroller || !slides?.length) return;

    const selectedIndex = Number.isInteger(index) ? index : slideshow.current;
    const slide =
      slides[selectedIndex ?? 0] ??
      this.querySelector("slideshow-slide[aria-hidden='false']") ??
      slides[0];
    const ratio = this.#getSlideRatio(slide);
    const width = scroller.clientWidth || slideshow.refs.slideshowContainer?.clientWidth || slide.clientWidth;

    if (!ratio || !width) return;

    this.style.setProperty('--active-product-media-height', `${Math.ceil(width / ratio)}px`);
  }

  /**
   * @param {Element} slide
   */
  #getSlideRatio(slide) {
    const slideStyles = getComputedStyle(slide);
    const media = slide.querySelector('.product-media');

    return (
      this.#parseRatio(slideStyles.getPropertyValue('--media-preview-ratio')) ||
      this.#parseRatio(getComputedStyle(this).getPropertyValue('--gallery-aspect-ratio')) ||
      (media ? this.#parseRatio(getComputedStyle(media).getPropertyValue('--ratio')) : null)
    );
  }

  /**
   * @param {string} value
   */
  #parseRatio(value) {
    const ratio = value.trim();

    if (!ratio) return null;

    if (ratio.includes('/')) {
      const [width, height] = ratio.split('/').map((part) => Number.parseFloat(part.trim()));

      if (width > 0 && height > 0) return width / height;
    }

    const parsed = Number.parseFloat(ratio);

    return parsed > 0 ? parsed : null;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
